import React, { useCallback, useEffect, useRef, useState } from 'react';
import Block from '../../design-system/chat-blocks/Block';
import Pill from '../../design-system/primitives/Pill';
import Btn from '../../design-system/primitives/Btn';
import Segmented from '../../design-system/primitives/Segmented';
import Select from '../../design-system/primitives/Select';
import Icon from '../../design-system/primitives/Icon';
import HoldToReveal from './HoldToReveal';
import Expandable, { expandedMaxHeight } from './Expandable';
import { fmtCompact, fmtDateTime, fmtDuration, fmtMetric, fmtShape, fmtUsd, relTime, titleCase } from './format';

const PAGE_SIZE = 50;

const KIND_LABEL = {
  'web:analysis': 'Web analysis',
  'sdk:train': 'API fit', 'sdk:auto_run': 'Agent AutoRun', 'sdk:predict': 'API predict', 'sdk:forecast': 'API forecast',
  'sdk:preprocess': 'API preprocess', 'sdk:shap': 'API explain',
  'pipeline:inference': 'Pipeline inference', 'pipeline:retrain': 'Pipeline retrain', 'pipeline:forecast': 'Pipeline forecast',
};
const KIND_ICON = {
  'web:analysis': 'sparkles',
  'sdk:train': 'brain', 'sdk:auto_run': 'bot', 'sdk:predict': 'line-chart', 'sdk:forecast': 'trending-up',
  'sdk:preprocess': 'wand-2', 'sdk:shap': 'scan-search',
  'pipeline:inference': 'workflow', 'pipeline:retrain': 'refresh-cw', 'pipeline:forecast': 'trending-up',
};
const SOURCE_LABEL = { web: 'Web app', sdk: 'API', pipeline: 'Pipeline' };

// The kind without its source, which the Kind cell prints underneath.
const kindLabel = (source, kind) => {
  const label = (KIND_LABEL[`${source}:${kind}`] || titleCase(kind)).replace(/^(API|Pipeline|Web) /, '');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const Kind = ({ row }) => (
  <span className={`qa-kind qa-kind--${row.source}`}>
    <span className="qa-kind__icon"><Icon name={KIND_ICON[`${row.source}:${row.kind}`] || 'circle'} size={12} /></span>
    <span>{kindLabel(row.source, row.kind)}<br /><span className="qa-kind__sub">{SOURCE_LABEL[row.source]}</span></span>
  </span>
);

const STATUS_STYLE = {
  succeeded: { tone: 'green' },
  failed: { tone: 'neutral', style: { background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid transparent' } },
  warn: { tone: 'neutral', style: { background: 'var(--warn-50)', color: 'var(--warn-500)', border: '1px solid transparent' } },
  running: { tone: 'accent' },
  queued: { tone: 'accent' },
  in_progress: { tone: 'outline' },
  stopped: { tone: 'outline' },
};

const SOURCE_OPTIONS = [
  { value: '', label: 'All' }, { value: 'web', label: 'Web' }, { value: 'sdk', label: 'API' }, { value: 'pipeline', label: 'Pipelines' },
];
// Column filters, multi-select (values OR, columns AND; none ticked = all).
// Values mirror the backend's RUN_STATUSES and ROW_FILTERS (effort =
// settings.QBRAIN_RESOURCE_MODES); task, effort, flag and tag are judged on
// built rows, so the backend scans past the page for them.
const options = (values) => values.map((v) => ({ value: v, label: titleCase(v) }));
const STATUS_OPTIONS = [
  { value: 'failed', label: 'Failed' }, { value: 'succeeded', label: 'Succeeded' },
  { value: 'running', label: 'Running' }, { value: 'queued', label: 'Queued' }, { value: 'warn', label: 'Warning' },
  { value: 'in_progress', label: 'In progress' }, { value: 'stopped', label: 'Stopped' },
];
const TAG_OPTIONS = options(['internal', 'demo', 'deleted', 'pipeline']);
const EFFORT_OPTIONS = options(['auto', 'low', 'medium', 'high', 'max']);
const TASK_OPTIONS = options(['classification', 'regression', 'time_series']);
const FLAG_OPTIONS = options(['overfit', 'mode_collapse', 'clean']);
const NO_FILTERS = { source: '', kind: [], user: [], tag: [], status: [], effort: [], task_type: [], flag: [] };

// Kinds the selected source can produce; `forecast` is shared by API and pipelines.
const kindOptions = (source) => {
  const kinds = Object.keys(KIND_LABEL).map((k) => k.split(':')).filter(([s]) => !source || s === source);
  const unique = [...new Map(kinds.map(([s, kind]) => [kind, kindLabel(s, kind)]))];
  return unique.map(([value, label]) => ({ value, label }));
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || { tone: 'outline' };
  return <Pill tone={s.tone} style={{ padding: '2px 8px', fontSize: 11, ...s.style }} dot={status === 'running'}>{titleCase(status)}</Pill>;
};

const Flag = ({ children }) => (
  <span className="qa-flag">{children}</span>
);

const Metric = ({ metric }) => {
  if (!metric || !metric.name) return <span className="qa-muted">—</span>;
  const isCompare = 'old' in metric;
  return (
    <span className="qa-mono qa-nowrap" title={isCompare ? 'before → after retrain' : 'train / test'}>
      <span className="qa-muted">{metric.name}</span>{' '}
      {isCompare
        ? <>{fmtMetric(metric.old)} → {fmtMetric(metric.new)}</>
        : <>{fmtMetric(metric.train)} <span className="qa-muted">/</span> <b>{fmtMetric(metric.test)}</b></>}
    </span>
  );
};

const Effort = ({ effort }) => {
  if (!effort || (!effort.requested && !effort.realized)) return <span className="qa-muted">—</span>;
  const same = !effort.realized || effort.requested === effort.realized;
  return (
    <span className="qa-mono qa-nowrap">
      {effort.requested || '?'}
      {!same && <span className="qa-muted"> → {effort.realized}</span>}
    </span>
  );
};

// LLM spend (from the analysis' token usage) plus GPU time at the configured
// rate; either may be absent, and the breakdown sits under the total.
const Cost = ({ row }) => {
  const llm = row.tokens?.cost_usd;
  const gpu = row.gpu_cost_usd;
  if (llm == null && gpu == null) return <span className="qa-muted">—</span>;
  const total = (llm || 0) + (gpu || 0);
  const parts = [];
  if (row.tokens) parts.push(`${fmtCompact(row.tokens.output)} tok`);
  if (gpu != null) parts.push(`GPU ${fmtUsd(gpu)}`);
  return (
    <span>
      {fmtUsd(total)}
      <br /><span className="qa-muted" style={{ fontSize: 11 }}>{parts.join(' · ')}</span>
    </span>
  );
};

const Details = ({ row }) => {
  const kv = [
    ...Object.entries(row.settings || {}),
    ...(row.winner ? Object.entries(row.winner).filter(([k]) => !['method', 'model_type'].includes(k)) : []),
    ...(row.pipeline ? Object.entries(row.pipeline) : []),
  ].filter(([, v]) => v != null && v !== '' && v !== false);
  return (
    <div className="qa-details">
      {kv.map(([k, v]) => (
        <span key={k} className="qa-kv"><span className="qa-kv__k">{titleCase(k)}</span><span className="qa-kv__v qa-mono">{Array.isArray(v) ? v.join(', ') : String(v)}</span></span>
      ))}
      {row.error?.message && <div className="qa-details__error">{row.error.code && <b>{row.error.code}: </b>}{row.error.message}</div>}
      {kv.length === 0 && !row.error && <span className="qa-muted">No further details recorded.</span>}
    </div>
  );
};

/**
 * One feed over web analyses, API jobs and pipeline runs. Either `fetchPage`
 * (paged, filterable) or a fixed `rows` list (compact embeds, e.g. recent failures).
 */
// Fresh page 1 in front of the older rows already loaded beyond it. Loaded
// rows inside page 1's time span that it no longer contains stopped matching
// (e.g. a status changed), so they go.
const mergeFresh = (prev, fresh, complete) => {
  const ids = new Set(fresh.map((r) => r.id));
  const oldest = fresh.length ? fresh[fresh.length - 1].created_at : null;
  const tail = complete || !oldest ? [] : prev.filter((r) => !ids.has(r.id) && r.created_at < oldest);
  return [...fresh, ...tail];
};

const filterParams = (filters) => Object.fromEntries(Object.entries(filters)
  .filter(([, v]) => v.length)
  // Multi-value filters travel comma-separated.
  .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v]));

const RunsTable = ({ fetchPage, refreshKey, rows: fixedRows, showUser = false, users = [], title = 'Analyses and API calls', icon = 'list', maxHeight = 560, onSelectUser }) => {
  const [rows, setRows] = useState(fixedRows || []);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(!!fetchPage);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [open, setOpen] = useState(null);
  // Only the newest request may write rows (a filter change outruns a refresh).
  const requestId = useRef(0);
  const refreshing = useRef(false);

  const load = useCallback((nextPage, append) => {
    if (!fetchPage) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    fetchPage({ page: nextPage, page_size: PAGE_SIZE, ...filterParams(filters) })
      .then((data) => {
        if (id !== requestId.current) return;
        setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
        setHasMore(data.has_more);
        setTruncated(data.truncated);
        setPage(nextPage);
      })
      .catch(() => { if (id === requestId.current) setError('Could not load runs.'); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
  }, [fetchPage, filters]);

  useEffect(() => { if (fetchPage) load(1, false); }, [load, fetchPage]);

  // Background refresh on the page's clock: re-read page 1 only and keep the
  // pages loaded beyond it. Skipped while any request is out; errors stay
  // silent since the page's live dot reports them.
  useEffect(() => {
    if (!fetchPage || !refreshKey || loading || refreshing.current) return;
    const id = ++requestId.current;
    refreshing.current = true;
    fetchPage({ page: 1, page_size: PAGE_SIZE, ...filterParams(filters) })
      .then((data) => {
        if (id !== requestId.current) return;
        if (page === 1) {
          setRows(data.rows);
          setHasMore(data.has_more);
        } else {
          setRows((prev) => mergeFresh(prev, data.rows, !data.has_more));
        }
        setTruncated(data.truncated);
      })
      .catch(() => {})
      .finally(() => { refreshing.current = false; });
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (fixedRows) setRows(fixedRows); }, [fixedRows]);

  const setFilter = (name) => (value) => setFilters((f) => ({ ...f, [name]: value }));
  // Drop kinds the new source cannot produce; they would only ever match nothing.
  const setSource = (source) => setFilters((f) => {
    const kinds = kindOptions(source).map((o) => o.value);
    return { ...f, source, kind: f.kind.filter((k) => kinds.includes(k)) };
  });
  const filtered = Object.values(filters).some((v) => v.length);

  const badge = fetchPage ? (
    <div className="qa-runs__filters">
      {filtered && <Btn size="xs" kind="ghost" onClick={() => setFilters(NO_FILTERS)}>Clear filters</Btn>}
      <Segmented options={SOURCE_OPTIONS} value={filters.source} onChange={setSource} />
    </div>
  ) : <Pill tone="neutral">{rows.length}</Pill>;

  const columns = [
    { key: 'chevron' },
    { key: 'when', label: 'When' },
    { key: 'kind', label: 'Kind', options: kindOptions(filters.source) },
    showUser && { key: 'user', label: 'User', options: users.map((u) => ({ value: u.id, label: u.username })) },
    { key: 'tag', label: 'Analysis', options: TAG_OPTIONS },
    { key: 'status', label: 'Status', options: STATUS_OPTIONS },
    { key: 'dataset', label: 'Dataset' },
    { key: 'effort', label: 'Effort', options: EFFORT_OPTIONS },
    { key: 'task_type', label: 'Task', options: TASK_OPTIONS },
    { key: 'metric', label: 'Metric' },
    { key: 'runtime', label: 'Runtime' },
    { key: 'flag', label: 'Flags', options: FLAG_OPTIONS },
    { key: 'model', label: 'Model' },
    { key: 'cost', label: 'Cost', className: 'qa-r' },
    { key: 'error', label: 'Error' },
  ].filter(Boolean);

  return (
    <Expandable>{(expanded) => (
    <Block icon={icon} title={title} badge={badge} footer={
      <>
        {fetchPage
          ? <span style={{ flex: 1 }}>{rows.length} shown{hasMore ? ' · more available' : ''}{truncated ? ' · filtered within the newest runs only' : ''}</span>
          : <span style={{ flex: 1 }} />}
        {fetchPage && hasMore && <Btn size="xs" kind="soft" onClick={() => load(page + 1, true)} disabled={loading}>Load more</Btn>}
        <span style={{ width: 30 }} />
      </>
    }>
      {error && <p className="qa-error">{error} <button type="button" className="qa-link" onClick={() => load(1, false)}>Retry</button></p>}
      <div className="qa-table-wrap" style={{ maxHeight: expanded ? expandedMaxHeight : maxHeight }}>
        <table className="qa-table qa-table--runs">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.className}>
                  {c.label}
                  {fetchPage && c.options && (
                    <span className="qa-th-filter">
                      <Select multiple icon="filter" options={c.options} value={filters[c.key]} onChange={setFilter(c.key)} ariaLabel={`${c.label} filter`} />
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const expanded = open === r.id;
              const isTraining = ['analysis', 'train', 'auto_run', 'retrain'].includes(r.kind);
              return (
                <React.Fragment key={r.id}>
                  <tr className={`qa-row ${r.status === 'failed' ? 'qa-row--failed' : ''}`} onClick={() => setOpen(expanded ? null : r.id)}>
                    <td className="qa-chevron"><Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={13} /></td>
                    <td className="qa-mono qa-nowrap" title={fmtDateTime(r.created_at)}>{relTime(r.created_at)}</td>
                    <td><Kind row={r} /></td>
                    {showUser && (
                      <td>
                        {r.user ? (
                          <button type="button" className="qa-link" onClick={(e) => { e.stopPropagation(); onSelectUser && onSelectUser(r.user.id); }}>{r.user.username}</button>
                        ) : '—'}
                      </td>
                    )}
                    <td>
                      {r.analysis ? (
                        <div className="qa-cell-title">
                          <a href={`/app/analysis/${r.analysis.id}`} target="_blank" rel="noopener noreferrer" className="qa-link qa-mono" onClick={(e) => e.stopPropagation()}>
                            #{r.analysis.id}
                          </a>
                          {r.analysis.is_internal && <span className="qa-tag">internal</span>}
                          {r.analysis.is_demo && <span className="qa-tag">demo</span>}
                          {!r.analysis.is_active && <span className="qa-tag">deleted</span>}
                          {r.analysis.has_pipeline && <span className="qa-tag" title="Has a pipeline schedule"><Icon name="workflow" size={10} /></span>}
                        </div>
                      ) : <span className="qa-muted">—</span>}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="qa-mono qa-nowrap">{fmtShape(r.dataset)}</td>
                    <td><Effort effort={r.effort} /></td>
                    <td className="qa-nowrap qa-muted">{r.task_type ? titleCase(r.task_type) : '—'}</td>
                    <td><Metric metric={r.metric} /></td>
                    <td className="qa-mono qa-nowrap">{fmtDuration(r.duration_sec)}</td>
                    <td className="qa-nowrap">
                      {r.overfit && <Flag>overfit</Flag>}
                      {r.mode_collapse && <Flag>mode collapse</Flag>}
                      {isTraining && r.overfit === false && r.mode_collapse === false && <span className="qa-ok"><Icon name="check" size={12} /></span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <HoldToReveal value={r.winner?.method || r.winner?.model_type} hint={r.winner?.method && r.winner?.model_type ? r.winner.model_type : null} />
                    </td>
                    <td className="qa-r qa-mono qa-nowrap"><Cost row={r} /></td>
                    <td className="qa-error-cell" title={r.error?.message || ''}>{r.error ? (r.error.code || r.error.message || 'error') : ''}</td>
                  </tr>
                  {expanded && (
                    <tr className="qa-row-details">
                      <td colSpan={columns.length}><Details row={r} /></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length} className="qa-empty">No runs match.</td></tr>
            )}
            {loading && rows.length === 0 && (
              [0, 1, 2].map((i) => <tr key={i}><td colSpan={columns.length}><div className="qa-skeleton" /></td></tr>)
            )}
          </tbody>
        </table>
      </div>
    </Block>
    )}</Expandable>
  );
};

export default RunsTable;
